/**
 * Taxonomy Utility System
 * Handles collection metafield-based taxonomies for mega menus and other theme sections
 * 
 * Usage:
 * const taxonomy = new TaxonomyUtility();
 * const data = taxonomy.parseCollectionTaxonomies(collectionHandle);
 * taxonomy.renderTaxonomyColumns(container, data);
 */

class TaxonomyUtility {
  constructor() {
    this.cache = new Map();
    this.maxTaxonomies = 3; // Maximum taxonomies per collection
  }

  /**
   * Parse collection taxonomies from metafields
   * @param {string} collectionHandle - Collection handle to lookup
   * @param {Object} collectionData - Collection data with metafields
   * @returns {Object} Parsed taxonomy data
   */
  parseCollectionTaxonomies(collectionHandle, collectionData = null) {
    // Check cache first
    if (this.cache.has(collectionHandle)) {
      return this.cache.get(collectionHandle);
    }

    let taxonomyData = {
      hastaxonomies: false,
      taxonomies: [],
      fallbackItems: []
    };

    try {
      // If collection data provided, use it; otherwise try to find from DOM
      let metafieldData = null;
      
      if (collectionData && collectionData.taxonomies) {
        metafieldData = collectionData.taxonomies;
      } else {
        // Try to find taxonomy data in DOM - check both .taxonomy-data and .category-data
        let taxonomyScript = document.querySelector(`[data-collection="${collectionHandle}"] .taxonomy-data`);

        if (!taxonomyScript) {
          // Also check for category-data in mega menu
          taxonomyScript = document.querySelector(`[data-category="${collectionHandle}"] .category-data`);
        }

        if (taxonomyScript) {
          const scriptData = JSON.parse(taxonomyScript.textContent);

          // Handle different data structures
          if (scriptData.taxonomies) {
            // Data is in scriptData.taxonomies (from mega menu)
            metafieldData = { taxonomies: scriptData.taxonomies };
          } else {
            // Data is the taxonomies directly
            metafieldData = scriptData;
          }
        }
      }

      if (metafieldData && metafieldData.taxonomies && metafieldData.taxonomies.length > 0) {
        taxonomyData.hastaxonomies = true;
        taxonomyData.taxonomies = this.validateTaxonomies(metafieldData.taxonomies);
      } else if (collectionData && collectionData.subcategories) {
        // Fallback to flat subcategories
        taxonomyData.fallbackItems = collectionData.subcategories;
      }

    } catch (error) {
      console.warn(`Error parsing taxonomies for collection ${collectionHandle}:`, error);
      // Fallback to subcategories if available
      if (collectionData && collectionData.subcategories) {
        taxonomyData.fallbackItems = collectionData.subcategories;
      }
    }

    // Cache the result
    this.cache.set(collectionHandle, taxonomyData);
    return taxonomyData;
  }

  /**
   * Validate and sanitize taxonomy data
   * @param {Array} taxonomies - Raw taxonomy data
   * @returns {Array} Validated taxonomies
   */
  validateTaxonomies(taxonomies) {
    if (!Array.isArray(taxonomies)) {
      return [];
    }

    return taxonomies
      .slice(0, this.maxTaxonomies) // Limit to max taxonomies
      .filter(taxonomy => {
        return taxonomy && 
               typeof taxonomy.name === 'string' && 
               taxonomy.name.trim() !== '' &&
               Array.isArray(taxonomy.collections) &&
               taxonomy.collections.length > 0;
      })
      .map(taxonomy => ({
        name: taxonomy.name.trim(),
        handle: taxonomy.handle || this.createHandle(taxonomy.name),
        collections: this.validateCollections(taxonomy.collections)
      }));
  }

  /**
   * Validate collection items within a taxonomy
   * @param {Array} collections - Collection items
   * @returns {Array} Validated collections
   */
  validateCollections(collections) {
    if (!Array.isArray(collections)) {
      return [];
    }

    return collections
      .filter(collection => {
        return collection && 
               collection.filter_only !== true &&
               typeof collection.title === 'string' && 
               collection.title.trim() !== '' &&
               typeof collection.handle === 'string' &&
               collection.handle.trim() !== '';
      })
      .map(collection => {
        let url = collection.seo_path || collection.url;

        // Fallback to handle-based URL if no seo_path or url
        if (!url && collection.handle) {
          url = `/collections/${collection.handle.trim()}`;
        }

        // Final fallback to prevent undefined
        if (!url) {
          url = '#';
          console.warn('No URL found for collection:', collection);
        }

        return {
          title: collection.title ? collection.title.trim() : 'Unknown',
          handle: collection.handle ? collection.handle.trim() : '',
          url: url
        };
      });
  }

  /**
   * Create a handle from a name
   * @param {string} name - Name to convert
   * @returns {string} Handle
   */
  createHandle(name) {
    return name.toLowerCase()
               .replace(/[^a-z0-9\s-]/g, '')
               .replace(/\s+/g, '-')
               .replace(/-+/g, '-')
               .trim('-');
  }

  /**
   * Render taxonomy columns in a container
   * @param {HTMLElement} container - Container element
   * @param {Object} taxonomyData - Parsed taxonomy data
   * @param {Object} options - Rendering options
   */
  renderTaxonomyColumns(container, taxonomyData, options = {}) {
    if (!container || !taxonomyData) {
      console.warn('Invalid container or taxonomy data provided');
      return;
    }

    const defaults = {
      showHeaders: true,
      maxItemsPerColumn: 10,
      columnClass: 'taxonomy-column',
      headerClass: 'taxonomy-header',
      itemClass: 'taxonomy-item',
      linkClass: 'taxonomy-link'
    };

    const config = { ...defaults, ...options };

    // Clear container
    container.innerHTML = '';

    if (taxonomyData.hastaxonomies && taxonomyData.taxonomies.length > 0) {
      // Render taxonomy columns
      this.renderTaxonomyMode(container, taxonomyData.taxonomies, config);
    } else if (taxonomyData.fallbackItems.length > 0) {
      // Render flat list mode
      this.renderFlatMode(container, taxonomyData.fallbackItems, config);
    } else {
      // Show placeholder
      this.renderPlaceholder(container);
    }
  }

  /**
   * Render taxonomy columns mode
   * @param {HTMLElement} container - Container element
   * @param {Array} taxonomies - Taxonomy data
   * @param {Object} config - Configuration options
   */
  renderTaxonomyMode(container, taxonomies, config) {
    container.classList.add('taxonomy-mode');

    // Create a container for the taxonomy columns
    const columnsContainer = document.createElement('div');
    columnsContainer.className = 'vevor-taxonomy-columns-container';

    taxonomies.forEach(taxonomy => {
      const column = document.createElement('div');
      column.className = config.columnClass;

      if (config.showHeaders) {
        const header = document.createElement('h4');
        header.className = config.headerClass;
        header.textContent = taxonomy.name;
        column.appendChild(header);
      }

      // Determine if we need multiple sub-columns
      const maxItemsPerSubColumn = 8; // Max items per sub-column
      const totalItems = taxonomy.collections.length;
      const needsMultipleColumns = totalItems > maxItemsPerSubColumn;

      if (needsMultipleColumns) {
        // Create sub-columns container
        const subColumnsContainer = document.createElement('div');
        subColumnsContainer.className = 'taxonomy-sub-columns';

        // Split items into chunks
        const chunks = [];
        for (let i = 0; i < totalItems; i += maxItemsPerSubColumn) {
          chunks.push(taxonomy.collections.slice(i, i + maxItemsPerSubColumn));
        }

        // Create sub-columns
        chunks.forEach(chunk => {
          const subColumn = document.createElement('div');
          subColumn.className = 'taxonomy-sub-column';

          chunk.forEach(collection => {
            this.createTaxonomyItem(collection, subColumn, config);
          });

          subColumnsContainer.appendChild(subColumn);
        });

        // Add a class to indicate this column has sub-columns
        column.classList.add('has-sub-columns');
        column.appendChild(subColumnsContainer);
      } else {
        // Single column layout
        const itemsToShow = taxonomy.collections.slice(0, maxItemsPerSubColumn);

        itemsToShow.forEach(collection => {
          this.createTaxonomyItem(collection, column, config);
        });
      }

      columnsContainer.appendChild(column);
    });

    // Append the columns container to the main container
    container.appendChild(columnsContainer);
  }

  /**
   * Create a taxonomy item element
   * @param {Object} collection - Collection data
   * @param {HTMLElement} container - Container to append to
   * @param {Object} config - Configuration options
   */
  createTaxonomyItem(collection, container, config) {
    // Process the URL - use seo_path if available, fallback to url, then generate from handle
    let url = collection.seo_path || collection.url;
    if (!url && collection.handle) {
      url = `/collections/${collection.handle}`;
    }
    if (!url) {
      url = '#';
      console.warn('No URL found for collection:', collection);
    }

    const item = document.createElement('div');
    item.className = config.itemClass;

    const link = document.createElement('a');
    link.className = config.linkClass;
    link.href = url;
    link.textContent = collection.title;

    item.appendChild(link);
    container.appendChild(item);
  }

  /**
   * Render flat list mode (backward compatibility)
   * @param {HTMLElement} container - Container element
   * @param {Array} items - Flat list items
   * @param {Object} config - Configuration options
   */
  renderFlatMode(container, items, config) {
    container.classList.add('flat-mode');
    
    items.forEach(item => {
      const itemElement = document.createElement('div');
      itemElement.className = config.itemClass;
      
      const link = document.createElement('a');
      link.className = config.linkClass;
      link.href = item.url;
      link.textContent = item.title;
      
      itemElement.appendChild(link);
      container.appendChild(itemElement);
    });
  }

  /**
   * Render placeholder content
   * @param {HTMLElement} container - Container element
   */
  renderPlaceholder(container) {
    container.innerHTML = `
      <div class="taxonomy-placeholder">
        <p>No subcategories available</p>
      </div>
    `;
  }

  /**
   * Clear cache for a specific collection or all collections
   * @param {string|null} collectionHandle - Collection handle or null for all
   */
  clearCache(collectionHandle = null) {
    if (collectionHandle) {
      this.cache.delete(collectionHandle);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get taxonomy data for use in other theme sections
   * @param {string} collectionHandle - Collection handle
   * @returns {Object} Taxonomy data
   */
  getTaxonomyData(collectionHandle) {
    return this.parseCollectionTaxonomies(collectionHandle);
  }
}

// Initialize global instance
window.TaxonomyUtility = TaxonomyUtility;

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (!window.taxonomyUtility) {
    window.taxonomyUtility = new TaxonomyUtility();
    // Dispatch event to notify other scripts that taxonomy utility is ready
    window.dispatchEvent(new CustomEvent('taxonomyUtilityReady'));
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = TaxonomyUtility;
}
